import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import PaperClipOutlinedIcon from '@ant-design/icons/PaperClipOutlined'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import Section from '@pages/VisitV2/VisitNote/SOAP/components/Section'

import AddMenu from './AddMenu'
import { StagedTile, tileGridStyles, UploadedTile } from './AttachmentTile'
import DragOverlay from './DragOverlay'
import DropStrip from './DropStrip'
import LinkDocumentDialog from './LinkDocumentDialog'
import QueueHint from './QueueHint'
import { useAttachmentQueue, useFileIntake, useTaskFileRemove, useTaskFiles } from './TaskAttachments.hooks'
import UploadDialog from './UploadDialog'

export default function TaskAttachments({ task, disabled = false, uploadOnly = false, variant = 'section' }) {
  const rootRef = useRef(null)
  const [linkOpen, setLinkOpen] = useState(false)

  const { data, isPending } = useTaskFiles(task.id)
  const remove = useTaskFileRemove(task.id)
  const queue = useAttachmentQueue()
  const { dragging, scope } = useFileIntake({ rootRef, onFiles: queue.add, disabled })

  const canLink = !uploadOnly && Boolean(task.patientId)
  const linkedIds = useMemo(() => new Set((data ?? []).filter((d) => d.fileType === 'UsersFile').map((d) => d.file.id)), [data])

  const count = data?.length ?? 0
  const showEmpty = !isPending && count === 0

  if (variant === 'block' && disabled && showEmpty) return null

  const addMenu = !disabled && (
    <AddMenu
      onFiles={queue.add}
      onLink={canLink ? () => setLinkOpen(true) : undefined}
      variant={variant === 'block' ? 'text' : 'contained'}
    />
  )

  const content = (
    <Stack ref={rootRef} spacing={1} sx={{ position: 'relative' }}>
      {isPending && <Skeleton variant="rounded" height={132} sx={{ width: 172 }} />}
      {count > 0 && (
        <Box sx={tileGridStyles}>
          {data.map(({ id, file, fileType }) => (
            <UploadedTile
              key={id}
              file={file}
              taskOwned={fileType === 'upload'}
              onRemove={disabled ? undefined : () => remove.mutateAsync(id)}
            />
          ))}
        </Box>
      )}
      {showEmpty && disabled && (
        <Paper variant="outlined" sx={styles.empty}>
          <Typography variant="h4" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
            No files attached
          </Typography>
        </Paper>
      )}
      {!disabled && <DropStrip onFiles={queue.add} />}
      {dragging && scope && createPortal(<DragOverlay identifier={task.identifier} />, scope)}
    </Stack>
  )

  const dialogs = (
    <>
      {!disabled && <UploadDialog task={task} queue={queue} />}
      {canLink && <LinkDocumentDialog task={task} linkedIds={linkedIds} open={linkOpen} onClose={() => setLinkOpen(false)} />}
    </>
  )

  if (variant === 'block') {
    return (
      <Stack spacing={1} sx={styles.block}>
        <Stack direction="row" sx={styles.blockHeader}>
          <Typography variant="caption" sx={styles.blockLabel}>
            Attachments{count > 0 ? ` · ${count}` : ''}
          </Typography>
          {addMenu}
        </Stack>
        {content}
        {dialogs}
      </Stack>
    )
  }

  return (
    <>
      <Section
        id="section-task-attachments"
        label="Attachments"
        count={count || undefined}
        icon={<PaperClipOutlinedIcon />}
        action={addMenu}
      >
        <Box sx={{ p: 1 }}>{content}</Box>
      </Section>
      {dialogs}
    </>
  )
}

export function AttachmentsField({ queue, disabled = false }) {
  const rootRef = useRef(null)
  const { dragging, scope } = useFileIntake({ rootRef, onFiles: queue.add, disabled })

  return (
    <Stack ref={rootRef} spacing={1} sx={{ position: 'relative' }}>
      <Typography sx={{ color: 'text.secondary' }}>Attachments</Typography>
      <DropStrip onFiles={queue.add} disabled={disabled} />
      {queue.items.length > 0 && (
        <>
          <Box sx={tileGridStyles}>
            {queue.items.map((item) => (
              <StagedTile
                key={item.id}
                item={item}
                onPreviewed={() => queue.markPreviewed(item.id)}
                onRemove={disabled ? undefined : () => queue.remove(item.id)}
                onNameChange={(name) => queue.setName(item.id, name)}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            <QueueHint queue={queue} deferred />
          </Typography>
        </>
      )}
      {dragging && scope && createPortal(<DragOverlay />, scope)}
    </Stack>
  )
}

const styles = {
  empty: {
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  block: {
    p: 1.5,
    borderRadius: 1,
    backgroundColor: 'background.default',
  },
  blockHeader: {
    gap: 1,
    minHeight: 30,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  blockLabel: {
    color: 'text.secondary',
    textTransform: 'uppercase',
  },
}
